// mds-dojo — dojo/_base/lang
// Faithful slim of the dojo/_base/lang functions the oracle uses:
// mixin, hitch, clone, getObject, setObject, delegate, extend, trim, is* guards.

function _mixin(dest: any, source: any): any {
  const empty: any = {};
  for (const name in source) {
    const s = source[name];
    if (!(name in dest) || (dest[name] !== s && (!(name in empty) || empty[name] !== s))) {
      dest[name] = s;
    }
  }
  return dest;
}

export function mixin(dest: any, ...sources: any[]): any {
  if (!dest) { dest = {}; }
  for (const src of sources) {
    if (src) { _mixin(dest, src); }
  }
  return dest;
}

export function getObject(name: string, create?: boolean, context?: any): any {
  const parts = name.split('.');
  let obj = context || (globalThis as any);
  for (let i = 0; obj != null && i < parts.length; i++) {
    const p = parts[i];
    if (!(p in obj) && create) { obj[p] = {}; }
    obj = obj[p];
  }
  return obj; // undefined if not found (and not create)
}

export function setObject(name: string, value: any, context?: any): any {
  const parts = name.split('.');
  const p = parts.pop() as string;
  let obj = context || (globalThis as any);
  for (let i = 0; obj && i < parts.length; i++) {
    const part = parts[i];
    obj = (part in obj) ? obj[part] : (obj[part] = {});
  }
  obj[p] = value;
  return value;
}

export function partial(method: any, ...pre: any[]): (...a: any[]) => any {
  const fn = typeof method === 'string' ? (globalThis as any)[method] : method;
  return (...a: any[]) => fn.apply(null, pre.concat(a));
}

export function hitch(scope: any, method?: any, ...pre: any[]): (...a: any[]) => any {
  if (typeof scope === 'function' && method === undefined) {
    // hitch(fn) — bind to global
    method = scope;
    scope = null;
  }
  if (typeof method === 'string') {
    scope = scope || (globalThis as any);
    return function (this: any, ...args: any[]) {
      return scope[method].apply(scope, pre.concat(args));
    };
  }
  return function (this: any, ...args: any[]) {
    return method.apply(scope || this, pre.length ? pre.concat(args) : args);
  };
}

export function isArray(it: any): it is any[] {
  return Array.isArray(it);
}

export function isFunction(it: any): boolean {
  return typeof it === 'function';
}

export function isString(it: any): it is string {
  return typeof it === 'string' || it instanceof String;
}

export function isObject(it: any): boolean {
  return it !== undefined && (it === null || typeof it === 'object' || isArray(it) || isFunction(it));
}

// Added for the dijit/Tooltip faithful port (dijit/Tooltip calls lang.isArrayLike
// on its connectId param). Faithful to dojo/_base/lang.isArrayLike; purely additive.
export function isArrayLike(it: any): boolean {
  return it && it !== undefined && !isString(it) && !isFunction(it) &&
    !(it.tagName && it.tagName.toLowerCase() == 'form') &&
    (isArray(it) || isFinite((it as any).length));
}

export function clone<T>(src: T): T {
  if (!src || typeof src !== 'object' || isFunction(src)) {
    return src;
  }
  // DOM node — clone the node itself (faithful to real dojo/_base/lang). NEVER fall through
  // to a plain-object copy: for..in over a node walks parentNode/childNodes/ownerDocument →
  // effectively the whole DOM. cloneNode(true) is native and bounded to the node's subtree.
  if ((src as any).nodeType && typeof (src as any).cloneNode === 'function') {
    return (src as any).cloneNode(true);
  }
  // Fast path: native structuredClone (C++, cycle-safe, preserves Date/RegExp/Map/Set/
  // TypedArray) for plain data — what the hot callers pass (backend JSON, e.g. moreCore
  // SectionLite.setData clones its `data` twice). Profiling /Planning showed the old
  // recursive body was ~50% of active page-load CPU (megamorphic per-key writes).
  //
  // ONE attempt for the whole graph. If it throws — DataCloneError (functions, DOM nodes,
  // non-cloneable class instances) or ReferenceError (structuredClone absent) — fall back
  // to a pure-JS recursive copy that NEVER re-invokes structuredClone. That separation
  // matters: an earlier version re-attempted structuredClone at every recursion level, so a
  // graph with one non-cloneable member made EACH nested clone throw, and every thrown
  // DataCloneError captured a JS stack (SavedStacks::saveCurrentStack) — ~18% of active CPU
  // in a prod profile. Now: at most one throw per top-level clone(), then pure-JS copy.
  // Note: structuredClone copies only OWN enumerable props (the recursive path also walks
  // inherited enumerables) — a non-issue for the plain data dojo clones.
  try {
    return structuredClone(src);
  } catch {
    return cloneRecursive(src);
  }
}

// Pure-JS deep copy — the faithful fallback for inputs structuredClone can't handle.
// Deliberately recurses via itself (NOT clone()) so it never re-invokes structuredClone.
function cloneRecursive<T>(src: T): T {
  if (!src || typeof src !== 'object' || isFunction(src)) {
    return src;
  }
  if ((src as any).nodeType && typeof (src as any).cloneNode === 'function') {
    return (src as any).cloneNode(true); // DOM node — see clone(); don't for..in-walk it
  }
  if (src instanceof Date) {
    return new Date((src as any).getTime()) as any;
  }
  if (isArray(src)) {
    const arr: any[] = [];
    for (let i = 0; i < (src as any).length; i++) {
      if (i in (src as any)) { arr.push(cloneRecursive((src as any)[i])); }
    }
    return arr as any;
  }
  const dest: any = {};
  for (const key in src) {
    dest[key] = cloneRecursive((src as any)[key]);
  }
  return dest;
}

export function delegate(obj: any, props?: any): any {
  const d = Object.create(obj || null);
  if (props) { _mixin(d, props); }
  return d;
}

export function extend(ctor: any, ...sources: any[]): any {
  for (const src of sources) {
    _mixin(ctor.prototype, src);
  }
  return ctor;
}

export function trim(str: string): string {
  return String(str).replace(/^\s+/, '').replace(/\s+$/, '');
}

export function replace(tmpl: string, map: any): string {
  return tmpl.replace(/\{([^\}]+)\}/g, (_m, k) => {
    const v = getObject(k, false, map);
    return v == null ? '' : String(v);
  });
}

const lang = {
  partial,
  mixin, getObject, setObject, hitch, isArray, isFunction, isString, isObject,
  isArrayLike,
  clone, delegate, extend, trim, replace,
};
export default lang;
